import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  ShieldCheck,
  Star,
  Users,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  Sparkles,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  CircleDollarSign,
  Zap
} from "lucide-react";
import { submitSolarQuotation } from "../Services/userServices";

const ContactCTA = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    propertyType: "Residential Rooftop",
    monthlyBill: "",
    location: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const propertyOptions = [
    "Residential Rooftop",
    "Commercial Complex",
    "Industrial Grid"
  ];

  const validateForm = () => {
    if (formData.name.trim().length < 2) {
      setStatusMessage({ type: "error", text: "Please enter your full name." });
      return false;
    }
    const cleanedPhone = formData.phone.replace(/[\s\-\+]/g, "");
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(cleanedPhone.slice(-10))) {
      setStatusMessage({ type: "error", text: "Enter a valid 10-digit mobile number." });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setStatusMessage({ type: "error", text: "Enter a valid email address." });
      return false;
    }
    const bill = Number(formData.monthlyBill);
    if (!formData.monthlyBill || isNaN(bill) || bill <= 0) {
      setStatusMessage({ type: "error", text: "Enter your monthly electricity bill." });
      return false;
    }
    if (formData.location.trim().length < 2) {
      setStatusMessage({ type: "error", text: "Enter your city/location." });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage({ type: "", text: "" });
    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await submitSolarQuotation(formData);

    if (result.success) {
      setStatusMessage({ type: "success", text: "Solar evaluation requested successfully!" });
      setFormData({ name: "", phone: "", email: "", propertyType: "Residential Rooftop", monthlyBill: "", location: "" });
    } else {
      setStatusMessage({ type: "error", text: result.error || "Submission failed. Try again." });
    }
    setIsSubmitting(false);
  };

  const trustMetrics = [
    { icon: Users, title: "500+ Installations", desc: "Across India" },
    { icon: Star, title: "4.9/5 Rating", desc: "Verified Client Reviews" },
  ];

  return (
    <section id="contact" className="relative bg-[#040810] text-white py-12 sm:py-16 lg:py-20 overflow-hidden border-t border-[#1E5CB3]/20 font-sans">
      
      {/* 🌟 LUXURIOUS CYBER GRID OVERLAYS 🌟 */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} 
      />
      
      {/* Ambient Outer Space Glows */}
      <div className="absolute top-0 -left-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/10 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 -right-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#1E5CB3]/10 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Split Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* 🌟 LEFT COLUMN: VALUE PROPOSITION 🌟 */}
          <div className="lg:col-span-5 text-center lg:text-left flex flex-col items-center lg:items-start">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-[#0D3F7F]/30 border border-[#1E5CB3]/40 px-3.5 py-1.5 rounded-full backdrop-blur-md mb-4"
            >
              <Zap className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.15em] text-[#F0F7FF] uppercase">
                Free Solar Evaluation
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-2xl xs:text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-4"
            >
              Start Saving <br className="hidden lg:block" />
              <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">
                Money Today
              </span>
            </motion.h2>

            <p className="text-slate-400 leading-relaxed text-xs sm:text-sm lg:text-[14px] max-w-md font-medium mb-6 px-1 sm:px-0">
              Enter your property details below. Our engineers will create a customized 3D satellite solar layout tailored to your roof and daily energy consumption.
            </p>

            {/* Compact Trust Badges Grid (Responsive layouts) */}
            <div className="grid grid-cols-1 xs:grid-cols-2 lg:flex lg:flex-wrap gap-3 w-full max-w-md lg:max-w-none">
              {trustMetrics.map((metric, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl shadow-sm backdrop-blur-sm">
                  <metric.icon className="w-4 h-4 text-[#F7941D] shrink-0" />
                  <div className="text-left">
                    <h4 className="text-[11px] font-bold text-white leading-tight">{metric.title}</h4>
                    <p className="text-[9px] text-slate-400 font-semibold">{metric.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 🌟 RIGHT COLUMN: THE ULTIMATE CYBER-GLASS EVALUATION BOX 🌟 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 w-full relative"
          >
            {/* Outer Glow Frame */}
            <div className="absolute -inset-0.5 rounded-[1.25rem] sm:rounded-[1.5rem] bg-gradient-to-r from-[#0D3F7F] via-[#F7941D] to-[#0D3F7F] opacity-15 blur-lg pointer-events-none" />

            <div className="bg-[#08101E]/95 backdrop-blur-xl border border-white/10 p-4 xs:p-6 sm:p-8 rounded-[1.25rem] sm:rounded-[1.5rem] shadow-2xl relative z-10">
              
              <div className="mb-5 sm:mb-6 flex flex-row items-center justify-between gap-2 border-b border-white/10 pb-4">
                <div>
                  <h3 className="text-lg sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
                    Request Blueprint <Sparkles className="w-4 h-4 text-[#FFC107] shrink-0" />
                  </h3>
                </div>
                <div className="text-[9px] sm:text-[10px] bg-[#1E5CB3]/20 text-[#60A5FA] font-bold px-2.5 py-1 rounded-md border border-[#1E5CB3]/30 uppercase tracking-widest">
                  100% Free
                </div>
              </div>

              {/* Status Message Alerts */}
              <AnimatePresence>
                {statusMessage.text && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    className={`p-3 rounded-lg flex items-center gap-2 border text-[11px] font-bold ${
                      statusMessage.type === "success"
                        ? "bg-[#10B981]/10 border-[#10B981]/20 text-[#10B981]"
                        : "bg-red-500/10 border-red-500/20 text-red-400"
                    }`}
                  >
                    {statusMessage.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                    <span>{statusMessage.text}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* COMPACT INTERACTIVE FORM */}
              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Full Name"
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] text-xs sm:text-[13px] font-semibold transition-colors"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="tel" required maxLength={10} value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+]/g, "") })}
                      placeholder="Mobile Number"
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] text-xs sm:text-[13px] font-semibold transition-colors"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email Address"
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] text-xs sm:text-[13px] font-semibold transition-colors"
                    />
                  </div>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text" required value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="City (e.g. Indore)"
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] text-xs sm:text-[13px] font-semibold transition-colors"
                    />
                  </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                  <div className="relative">
                    <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 z-10" />
                    <button
                      type="button" onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-slate-300 text-xs sm:text-[13px] font-bold outline-none flex items-center justify-between transition-colors focus:border-[#F7941D]"
                    >
                      <span className="truncate">{formData.propertyType}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isDropdownOpen ? "rotate-180 text-[#F7941D]" : ""}`} />
                    </button>

                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                          className="absolute z-50 left-0 right-0 mt-1.5 bg-[#0D1525] border border-white/10 rounded-xl overflow-hidden shadow-2xl"
                        >
                          {propertyOptions.map((option, index) => (
                            <button
                              key={index} type="button"
                              onClick={() => { setFormData({ ...formData, propertyType: option }); setIsDropdownOpen(false); }}
                              className={`w-full text-left px-4 py-3 sm:py-2.5 text-[11px] sm:text-[12px] font-bold transition-all flex items-center justify-between ${
                                formData.propertyType === option ? "bg-[#F7941D]/10 text-[#F7941D]" : "text-slate-300 hover:bg-white/5"
                              }`}
                            >
                              <span>{option}</span>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="relative">
                    <CircleDollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="number" required min="1" value={formData.monthlyBill}
                      onChange={(e) => setFormData({ ...formData, monthlyBill: e.target.value })}
                      placeholder="Monthly Bill (₹)"
                      className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-10 pr-4 py-3 sm:py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] text-xs sm:text-[13px] font-semibold transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2 sm:pt-3">
                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full relative overflow-hidden rounded-xl p-[2px] font-bold transition-all duration-300 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 group"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#F7941D] opacity-70 group-hover:opacity-100 transition-opacity" />
                    <span className="relative flex items-center justify-center bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-xs sm:text-[13px] font-black py-3.5 px-4 rounded-[10px] tracking-widest uppercase shadow-inner">
                      {isSubmitting ? (
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="flex items-center gap-2">
                          Generate Solar Blueprint <Send className="w-4 h-4 shrink-0" />
                        </span>
                      )}
                    </span>
                  </button>
                </div>

                {/* Trust Footer */}
                <div className="flex items-center justify-center gap-1.5 text-[9px] sm:text-[10px] font-bold text-slate-500 pt-3">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#F7941D] shrink-0" />
                  <span>256-bit SSL Encrypted • Zero Spam Guarantee</span>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;